import { useParams } from 'react-router'
import { Node as RFNode } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { FetchResult } from '@apollo/client'
import {
  CreateLoafSlicesAndAssignedSourcesMutationVariables,
  EdgeInput,
  NodeInput,
  NodeStep,
  PipelineInput,
  SliceType,
  UpdatePipelineAndAssignedSourcesMutationVariables,
} from '@ui/__generated__/graphql'
import { showToast } from '@ui/components/elements/ToastManager'
import { DatableNode, Member, NodeTypeEnum } from '@ui/interfaces'
// import client from '@ui/lib/api/fetchGraphQL'
import { formatRFEdges, formatRFNode, formatSteps } from '@ui/lib/helpers/formatNodeEdges'
import {
  CreatePipelineNodesAndAssignedSourcesQuery,
  UpdatePipelineAndAssignedSourcesQuery,
} from '@ui/loaders/processor'
import {
  CreatePipelineWorkflowMutation,
  UpdatePipelineWorkflowAndSourcesMutation,
  UpdatePipelineWorkflowMutation,
} from '@ui/loaders/workflow'
import { useAuth } from '@ui/providers/AuthProvider'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { RouteNamesEnum } from '@ui/router/routes'
import { pipelineService } from '@ui/services'
import { useNodeAndEdgeStore, usePipelinesStore, useSaveUpdateStatusStore } from '@ui/stores'

import { useAppNavigation } from './useAppNavigation'

interface SlicesFromCreate {
  id: number
  oldId: string
  type: string
}

const useCreatePipeline = (): ((
  variables: CreateLoafSlicesAndAssignedSourcesMutationVariables
) => Promise<FetchResult>) => {
  const [createLoafSlicesAndAssignedSources] = CreatePipelineNodesAndAssignedSourcesQuery()

  return (variables) => {
    return createLoafSlicesAndAssignedSources({
      variables,
    })
  }
}

export const useSavePipeline = () => {
  const workflowEnabled = useFeatureFlag(FeatureFlagName.WorkflowUpdate)

  const isQueryRailsLive = useFeatureFlag(FeatureFlagName.QueryRailsLive)
  const { currentMember } = useAuth()

  const { instanceId } = useParams()
  const { navigateTo } = useAppNavigation()

  const [updatePipelineAndAssignedSourcesMutation] = UpdatePipelineAndAssignedSourcesQuery()
  const [updatePipelineWorkflowMutation] = workflowEnabled
    ? UpdatePipelineWorkflowAndSourcesMutation()
    : UpdatePipelineWorkflowMutation()

  const { setPipelineModified, setSaveButtonEnabled, assignedSourceIds } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      setPipelineModified: state.setPipelineModified,
      setSaveButtonEnabled: state.setSaveButtonEnabled,
      assignedSourceIds: state.assignedSourceIds,
    }))
  )

  const { nodes, edges, setLoafId, setLoafName, setLoafState, updateIds } = useNodeAndEdgeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
      setLoafId: state.setLoafId,
      setLoafName: state.setLoafName,
      setLoafState: state.setLoafState,
      updateIds: state.updateIds,
    }))
  )

  const { pipelines, setPipelines } = usePipelinesStore(
    useShallow((state) => ({
      pipelines: state.pipelines,
      setPipelines: state.setPipelines,
    }))
  )

  const createPipeline = useCreatePipeline()
  const [createPipelineWorkflow] = CreatePipelineWorkflowMutation()

  const save = async ({ enabled = false, name }: { enabled: boolean; name: string }) => {
    const firehose = nodes.find((n) => n.type === NodeTypeEnum.FIREHOSE)

    const slices = nodes
      .filter((n) => [NodeTypeEnum.CODE, NodeTypeEnum.EXPORT].includes(n.type as NodeTypeEnum))
      .map((node: RFNode) => {
        const data = node.data
        const nodeType = node.type

        return {
          id: node.id,
          parentSliceId: data.parentSliceId === firehose!.id ? null : data.parentSliceId,
          code: data.code,
          type: nodeType as SliceType,
          exportType: data.exportType,
          description: data.description,
          destinationId: data.destinationId,
        }
      })

    const loaf = {
      name,
      enabled,
    }

    const { data, errors } = await createPipeline({
      loaf: loaf,
      slices: slices,
      assignedSourceIds: Array.from(assignedSourceIds),
    })

    if (data && data.createLoafAndSlices) {
      const { id, name, enabled } = data.createLoafAndSlices
      setLoafId(id)
      setLoafName(name)
      setLoafState(enabled)
      setPipelines([
        ...pipelines,
        {
          id,
          name,
          enabled,
          // @ts-expect-error firehose is not defined in the pipeline type
          firehose: firehose ? { size: firehose.data.size } : { size: { logs: 0, spans: 0, metrics: 0 } },
          slices: slices as unknown as DatableNode[],
          numberOfSlices: slices.length,
          doesPipelineHaveErrors: false,
          assignedSources: [],
        },
      ])

      setPipelineModified(false)
      setSaveButtonEnabled(false)

      const newSlices = data?.createLoafAndSlices?.slices || []
      newSlices.forEach((newSlice: SlicesFromCreate) => {
        updateIds(newSlice.oldId, `${newSlice.id}`)
      })
    }
    return { data, errors }
  }

  const updatePipeline = async (pipelineId: string) => {
    const nodesToUpdate = nodes
      .map((node) => {
        const data = node.data

        if (node.type === NodeTypeEnum.PLACEHOLDER || node.type === NodeTypeEnum.FIREHOSE) {
          return null
        }

        return {
          id: node.id,
          parentSliceId: data.parentSliceId === NodeTypeEnum.FIREHOSE ? null : data.parentSliceId,
          code: data.code,
          description: data.description,
          type: node.type as SliceType,
          exportType: data.exportType,
          destinationId: data.destinationId,
        }
      })
      .filter((node) => node)

    try {
      const pipeline_id = pipelineId

      const variables: UpdatePipelineAndAssignedSourcesMutationVariables = {
        pipeline_id,
        nodes: nodesToUpdate,
        updateLoafSourcesInput: {
          loafId: pipeline_id,
          sourceIds: Array.from(assignedSourceIds),
        },
      }

      await updatePipelineAndAssignedSourcesMutation({ variables })

      setPipelineModified(false)
      setSaveButtonEnabled(false)
    } catch (err) {
      showToast.error('Failed to save pipeline')
    }
  }

  const handleUpdatePipeline = async (pipeline: PipelineInput, name?: string, enabled?: boolean) => {
    // Filter out placeholder nodes and create a lookup map at the same time
    const nodesToUpdateMap = new Map(
      nodes.filter((node) => node.type !== NodeTypeEnum.PLACEHOLDER).map((node) => [node.id, node])
    )

    // If edges target a node that is not a placeholder, keep them
    const edgesToUpdate = edges
      .filter((edge) => nodesToUpdateMap.has(edge.target))
      .map((edge) => formatRFEdges(edge, pipeline.id as string))

    const nodesToUpdate = Array.from(nodesToUpdateMap.values())
      .map((node) => formatRFNode(node, pipeline.id as string))
      .map((node) => {
        return {
          id: node.id,
          type: node.type,
          data: {
            ...node.data,
            steps: formatSteps(node.data?.steps as NodeStep[]),
          },
        }
      })

    return await updatePipelineWorkflowMutation({
      variables: {
        pipeline: {
          id: pipeline.id,
          name: name || pipeline.name,
          enabled: enabled || pipeline.enabled,
        },
        nodes: nodesToUpdate as NodeInput[],
        edges: edgesToUpdate as EdgeInput[],
        sourceIds: Array.from(assignedSourceIds),
      },
      onCompleted: () => {
        setPipelineModified(false)
        showToast.success('Pipeline has been saved.' + ' Changes may take a few minutes to apply.', {
          position: 'bottom-right',
        })
      },
      onError: (error) => {
        console.error(error)
        showToast.error(error.message)
      },
    })
  }

  const updateWorkflow = async ({
    pipeline,
    isNew = false,
    name,
    enabled,
  }: {
    pipeline: PipelineInput
    isNew?: boolean
    name?: string
    enabled?: boolean
  }) => {
    if (!pipeline && !isNew) {
      throw new Error('Pipeline ID is required')
    }

    if (isNew) {
      if (!name) {
        throw new Error('Pipeline name is required')
      }

      if (isQueryRailsLive) {
        const pipeline = await pipelineService.createPipeline(
          {
            name,
            enabled: enabled || false,
          },
          currentMember as Member
        )

        const updatedPipeline = await handleUpdatePipeline(pipeline)

        navigateTo(RouteNamesEnum.EXISTING_PIPELINE, { pipelineId: pipeline.id, instanceId })

        return updatedPipeline
      } else {
        return await createPipelineWorkflow({
          variables: {
            name,
            enabled,
          },
          onCompleted: async (data) => {
            const createdPipeline = data?.createPipelineWorkflow
            await handleUpdatePipeline(createdPipeline as PipelineInput)
            navigateTo(RouteNamesEnum.EXISTING_PIPELINE, { pipelineId: createdPipeline.id, instanceId })
          },
        })
      }
    }

    return await handleUpdatePipeline(pipeline, name, enabled)
  }

  return { save, updatePipeline, updateWorkflow }
}
